import { api, NotifyModelKey } from "./api";
import { showStars, WOULD_TAKE_AGAIN_ENABLED } from "./generics";
import { commentStatus, renderStarRating, makeCollapsible } from "./comments";
import { showAvgRating } from "./posts";
import { renderPostHocNotifyPrompt } from "./notifyEmail";

// Moduł "Koła naukowe" (na razie tylko WPiA UW) - odpowiednik subjects.ts/subjectComments.ts,
// ale znacznie okrojony: koło naukowe nie ma kategorii/semestru (nie pochodzi z importu USOS,
// tylko z wiki wydziałowej - patrz import_wpia_wiki_kola_naukowe.py), a jego opinie nie mają
// głosowania/zgłaszania/roku akademickiego/trudności - tylko gwiazdka + polecam/nie polecam
// (patrz KoloNaukoweComment w models.py: brak upvotes/downvotes/academic_year/difficulty
// CELOWO, nie przeoczenie - to nie jest potrzebne dla kół naukowych wg specyfikacji).

export interface KoloNaukowe {
    id: number; name: string; slug: string; description: string;
    avg_rating: number; number_of_comments: number; would_take_again_percent: number | null;
}

export interface KoloNaukoweComment {
    id: number; body: string; created: string; rating: number; status: commentStatus;
    would_take_again: boolean | null;
    // Patrz Comment.ai_rejection_reason w comments.ts - ten sam wzorzec.
    ai_rejection_reason: string;
}

export type KoloNaukoweSortField = "most_comments" | "name" | "rating";
export type KoloNaukoweCommentSortField = "best_rating" | "worst_rating" | "newest" | "oldest";
type ValidationResult = { valid: true } | { valid: false; errors: Record<string, string> };

export const COMMENT_BODY_MAX_LENGTH = 500;

export function sortKolaNaukowe(kola: KoloNaukowe[], by: KoloNaukoweSortField): KoloNaukowe[] {
    const sorted = [...kola];
    sorted.sort((a, b) => {
        switch (by) {
            case "name":
                return a.name.localeCompare(b.name);
            case "rating": {
                const diff = b.avg_rating - a.avg_rating;
                return diff !== 0 ? diff : b.number_of_comments - a.number_of_comments;
            }
            case "most_comments":
                return b.number_of_comments - a.number_of_comments;
            default:
                return 0;
        }
    });
    return sorted;
}

export function renderKoloNaukowe(kolo: KoloNaukowe, universitySlug: string, facultySlug: string): HTMLElement {
    const card = document.createElement("a");
    card.className = "comment-card";
    card.dataset.id = String(kolo.id);
    card.href = `/${universitySlug}/${facultySlug}/kola-naukowe/${kolo.slug}/`;

    const heading = document.createElement("h2");
    heading.textContent = kolo.name;
    card.append(heading);

    // Opis CELOWO nie na karcie listy (na wyraźną prośbę) - widoczny dopiero na stronie samego
    // koła (patrz kolo_naukowe_detail.html) - karta ma pokazywać samą nazwę + ocenę, tak jak
    // karta wykładowcy (posts.ts) nie pokazuje żadnej treści opinii.
    const meta = document.createElement("div");
    meta.className = "comment-card-meta";
    if (kolo.number_of_comments > 0) {
        meta.append(showAvgRating(kolo.avg_rating, kolo.number_of_comments, kolo.would_take_again_percent));
    } else {
        const noReviews = document.createElement("span");
        noReviews.className = "comment-card-no-reviews";
        noReviews.textContent = "Brak opinii";
        meta.append(noReviews);
    }
    card.append(meta);

    return card;
}

function renderCommentWouldTakeAgainBadge(comment: KoloNaukoweComment): HTMLElement | null {
    if (!WOULD_TAKE_AGAIN_ENABLED) return null;
    if (comment.would_take_again === null) return null;

    const badge = document.createElement("span");
    badge.className = "comment-would-take-again-badge";
    badge.classList.add(comment.would_take_again ? "would-take-again-yes" : "would-take-again-no");
    badge.textContent = comment.would_take_again ? "\u{1F44D} Poleca" : "\u{1F44E} Odradza";
    badge.title = comment.would_take_again
        ? "Autor(ka) poleciłby/poleciłaby zapisanie się do tego koła ponownie"
        : "Autor(ka) odradza zapisanie się do tego koła";
    return badge;
}

export function whenAdded(comment: KoloNaukoweComment): HTMLElement {
    const container = document.createElement("div");
    container.className = "comment-date";
    const date = new Date(comment.created);
    container.textContent = date.toLocaleDateString("pl-PL", { year: "numeric", month: "short", day: "numeric" });
    return container;
}

// Bez menu/głosowania/zgłaszania (patrz notka przy modelu wyżej) - karta jest więc znacznie
// prostsza niż renderComment (comments.ts)/renderSubjectComment (subjectComments.ts).
export function renderKoloNaukoweComment(comment: KoloNaukoweComment): HTMLElement {
    const div = document.createElement("div");
    div.className = "comment";
    div.dataset.id = String(comment.id);

    const primaryGroup = document.createElement("div");
    primaryGroup.className = "comment-header-primary";
    primaryGroup.append(showStars(comment.rating));
    const badge = renderCommentWouldTakeAgainBadge(comment);
    if (badge) primaryGroup.appendChild(badge);

    const headerRow = document.createElement("div");
    headerRow.className = "comment-header-row";
    headerRow.append(primaryGroup);

    const body = document.createElement("p");
    body.style.whiteSpace = "pre-line";
    body.textContent = comment.body;

    div.append(headerRow, body, whenAdded(comment));
    return div;
}

export function sortKoloNaukoweComments(comments: KoloNaukoweComment[], by: KoloNaukoweCommentSortField): KoloNaukoweComment[] {
    const sorted = [...comments];
    sorted.sort((a, b) => {
        switch (by) {
            case "newest": return b.created.localeCompare(a.created);
            case "oldest": return a.created.localeCompare(b.created);
            case "best_rating": return b.rating - a.rating || b.created.localeCompare(a.created);
            case "worst_rating": return a.rating - b.rating || b.created.localeCompare(a.created);
            default: return 0;
        }
    });
    return sorted;
}

function validateComment(rating: number, body: string): ValidationResult {
    const errors: Record<string, string> = {};
    if (rating < 1 || rating > 5) errors.rating = "Wybierz ocenę.";
    if (body.length < 5 || body.length > COMMENT_BODY_MAX_LENGTH) {
        errors.body = `Treść opinii musi mieć od 5 do ${COMMENT_BODY_MAX_LENGTH} znaków.`;
    }
    return Object.keys(errors).length > 0 ? { valid: false, errors } : { valid: true };
}

// Kopia renderWouldTakeAgainField z subjectComments.ts (tam prywatna, nieeksportowana) -
// identyczny kształt, zduplikowana zamiast eksportowana stamtąd (ten sam wzorzec co REPORT_REASONS
// w subjectComments.ts - drobny prywatny kawałek UI, nie warto go przez to wiązać międzymodułowo).
function renderWouldTakeAgainField(): { element: HTMLElement, getSelection: () => boolean | undefined, reset: () => void } {
    const wrapper = document.createElement("div");
    wrapper.className = "would-take-again-field";

    const labelText = document.createElement("span");
    labelText.className = "field-label-text";
    labelText.textContent = "Czy poleciłbyś/poleciłabyś innym?";

    const buttonsRow = document.createElement("div");
    buttonsRow.className = "would-take-again-buttons";

    let selection: boolean | undefined = undefined;

    const yesBtn = document.createElement("button");
    yesBtn.type = "button";
    yesBtn.className = "would-take-again-btn";
    yesBtn.textContent = "Tak";

    const noBtn = document.createElement("button");
    noBtn.type = "button";
    noBtn.className = "would-take-again-btn";
    noBtn.textContent = "Nie";

    function updateActive(): void {
        yesBtn.classList.toggle("active", selection === true);
        noBtn.classList.toggle("active", selection === false);
    }

    yesBtn.addEventListener("click", () => { selection = true; updateActive(); });
    noBtn.addEventListener("click", () => { selection = false; updateActive(); });

    buttonsRow.append(yesBtn, noBtn);
    wrapper.append(labelText, buttonsRow);

    return {
        element: wrapper,
        getSelection: () => selection,
        reset: () => { selection = undefined; updateActive(); },
    };
}

export interface KoloNaukoweCommentFormOptions {
    onSubmitted?: (comment: KoloNaukoweComment) => void;
    onAutoClose?: () => void;
    onInstantSuccess?: (comment: KoloNaukoweComment) => void;
}

// Odpowiednik initSubjectCommentForm (subjectComments.ts), bez pola trudności/roku akademickiego.
export function initKoloNaukoweCommentForm(
    koloId: number,
    container: HTMLElement,
    onCommentAdded: (newComment: KoloNaukoweComment) => void,
    options: KoloNaukoweCommentFormOptions,
): void {
    container.replaceChildren();

    let selectedRating = 0;
    const form = document.createElement("form");

    const starRatingElement = renderStarRating(rating => { selectedRating = rating; });
    const ratingError = document.createElement("span");
    ratingError.className = "error";

    const wouldTakeAgainField = WOULD_TAKE_AGAIN_ENABLED ? renderWouldTakeAgainField() : null;
    const wouldTakeAgainError = document.createElement("span");
    wouldTakeAgainError.className = "error";

    const ratingFieldWrap = document.createElement("div");
    ratingFieldWrap.className = "rating-field";
    const ratingLabel = document.createElement("span");
    ratingLabel.className = "field-label-text";
    ratingLabel.textContent = "Ocena:";
    ratingFieldWrap.append(ratingLabel, starRatingElement.element);

    const fieldsRow = document.createElement("div");
    fieldsRow.className = "comment-form-row";
    fieldsRow.append(ratingFieldWrap);

    const bodyLabel = document.createElement("span");
    bodyLabel.className = "field-label-text comment-body-label";
    bodyLabel.textContent = "Twoja opinia:";

    const bodyTextarea = document.createElement("textarea");
    bodyTextarea.placeholder = "Co inni studenci powinni wiedzieć o tym kole naukowym?";
    bodyTextarea.required = true;
    bodyTextarea.rows = 3;

    const bodyError = document.createElement("span");
    bodyError.className = "error";

    const bodyFieldWrap = document.createElement("div");
    bodyFieldWrap.className = "comment-body-field";
    bodyFieldWrap.append(bodyLabel, bodyTextarea, bodyError);

    const submitBtn = document.createElement("button");
    submitBtn.type = "submit";
    submitBtn.textContent = "Podziel się opinią";

    const bottomRow = document.createElement("div");
    bottomRow.className = "comment-form-bottom-row";
    bottomRow.appendChild(submitBtn);

    const feedback = document.createElement("p");
    const notifyPromptContainer = document.createElement("div");

    const fieldsWrapChildren = [fieldsRow, ratingError, bodyFieldWrap];
    if (wouldTakeAgainField) fieldsWrapChildren.push(wouldTakeAgainField.element, wouldTakeAgainError);
    fieldsWrapChildren.push(bottomRow);
    const fieldsWrap = makeCollapsible(...fieldsWrapChildren);
    const feedbackWrap = makeCollapsible(feedback, notifyPromptContainer);
    form.append(fieldsWrap.outer, feedbackWrap.outer);

    let feedbackHideTimer: number | undefined;

    form.addEventListener("submit", async (event: Event) => {
        event.preventDefault();
        ratingError.textContent = "";
        wouldTakeAgainError.textContent = "";
        bodyError.textContent = "";
        feedback.textContent = "";
        feedback.classList.remove("error");
        notifyPromptContainer.replaceChildren();
        if (feedbackHideTimer !== undefined) {
            window.clearTimeout(feedbackHideTimer);
            feedbackHideTimer = undefined;
        }
        feedbackWrap.outer.classList.remove("collapsible-collapsed");
        container.classList.remove("comment-form-collapsed");

        const bodyValue = bodyTextarea.value.trim();
        const validation = validateComment(selectedRating, bodyValue);
        if (!validation.valid) {
            if (validation.errors.rating) ratingError.textContent = validation.errors.rating;
            if (validation.errors.body) bodyError.textContent = validation.errors.body;
            return;
        }

        const wouldTakeAgain = wouldTakeAgainField ? wouldTakeAgainField.getSelection() : null;
        if (wouldTakeAgain === undefined) {
            wouldTakeAgainError.textContent = "Zaznacz odpowiedź.";
            return;
        }

        submitBtn.disabled = true;
        feedback.textContent = "Zapisywanie...";

        const result = await api.createKoloNaukoweComment(koloId, {
            rating: selectedRating,
            body: bodyValue,
            would_take_again: wouldTakeAgain,
        });

        if (result.ok) {
            options.onSubmitted?.(result.data.comment);

            const notifyTargets: { model: NotifyModelKey; pk: number }[] = [];

            function scheduleFullHide(): void {
                if (feedbackHideTimer !== undefined) window.clearTimeout(feedbackHideTimer);
                feedbackHideTimer = window.setTimeout(() => {
                    feedbackWrap.outer.classList.add("collapsible-collapsed");
                    container.classList.add("comment-form-collapsed");
                    options.onAutoClose?.();
                }, 3000);
            }

            if (result.data.comment.status === "active") {
                onCommentAdded(result.data.comment);
                feedback.textContent = "Dziękujemy za podzielenie się swoją opinią";
            } else if (result.data.comment.status === "awaiting_moderation") {
                feedback.textContent = result.data.comment.ai_rejection_reason
                    ? "Nasz system wykrył potencjalny problem z treścią opinii, dlatego przed publikacją sprawdzi ją moderator."
                    : "Opinia została zapisana i zostanie opublikowana po sprawdzeniu przez moderację.";
                notifyTargets.push({ model: "kolo_naukowe_comment", pk: result.data.comment.id });
            }

            const notifyPrompts: HTMLElement[] = notifyTargets.length > 0
                ? [renderPostHocNotifyPrompt(notifyTargets, scheduleFullHide)]
                : [];
            notifyPromptContainer.replaceChildren(...notifyPrompts);

            starRatingElement.reset();
            wouldTakeAgainField?.reset();
            form.reset();

            fieldsWrap.outer.classList.add("collapsible-collapsed");
            if (notifyPrompts.length === 0) {
                if (options.onInstantSuccess) {
                    options.onInstantSuccess(result.data.comment);
                } else {
                    feedbackHideTimer = window.setTimeout(() => {
                        feedbackWrap.outer.classList.add("collapsible-collapsed");
                        container.classList.add("comment-form-collapsed");
                        options.onAutoClose?.();
                    }, 8000);
                }
            }
        } else {
            feedback.classList.add("error");
            feedback.textContent = `Błąd: ${result.error}`;
        }
        submitBtn.disabled = false;
    });

    container.appendChild(form);
}
